import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { AlertTriangleIcon, CheckCircleIcon, XCircleIcon, BriefcaseIcon, ShieldIcon, SunIcon, BackpackIcon, ZapIcon, ChevronDownIcon, LockIcon, SmartphoneIcon, StarIcon, HeartIcon, BrainIcon, ClockIcon, X, FileTextIcon, ShieldCheckIcon } from 'lucide-react';
function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 28
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 28
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
}
function CTAButton({
  label = '¡QUIERO EL MALETÍN AHORA!'
}: {
  label?: string;
}) {
  return <motion.a href="https://101goldproducts.com/cart/52111695380802:1" target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} className="block w-full text-center bg-[#e8735a] hover:bg-[#d45f46] text-white font-black text-lg md:text-xl py-5 px-8 rounded-2xl shadow-lg shadow-[#e8735a]/30 transition-colors cursor-pointer" data-id="element-3">
      {label}
    </motion.a>;
}
function SectionDivider() {
  return <div className="flex items-center gap-3 my-2" data-id="element-4">
      <div className="flex-1 h-px bg-[#0d7377]/15" data-id="element-5" />
      <div className="w-2 h-2 rounded-full bg-[#e8735a]/50" data-id="element-6" />
      <div className="flex-1 h-px bg-[#0d7377]/15" data-id="element-7" />
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-[#0d7377]/20 rounded-xl overflow-hidden" data-id="element-8">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-[#e8f5f5] transition-colors" aria-expanded={open} data-id="element-9">
        <span className="font-700 text-[#1c1c2e] text-sm md:text-base pr-4" data-id="element-10">
          {question}
        </span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.25
      }} className="flex-shrink-0" data-id="element-11">
          <ChevronDownIcon className="w-5 h-5 text-[#0d7377]" data-id="element-12" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-13">
        <p className="px-4 pb-4 pt-2 text-[#5a5a72] text-sm md:text-base bg-white" data-id="element-14">
          {answer}
        </p>
      </motion.div>
    </div>;
}
type ModalType = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que recopilamos',
    body: 'Al realizar una compra del Maletín de Calma Escolar, recopilamos únicamente la información necesaria para procesar tu pedido: nombre, dirección de correo electrónico y datos de pago procesados de forma segura por nuestra plataforma de pago. No almacenamos datos de tarjetas de crédito en nuestros servidores.'
  }, {
    heading: '2. Uso de la información',
    body: 'La información recopilada se utiliza exclusivamente para: (a) entregar el producto digital adquirido, (b) enviar confirmaciones de compra y actualizaciones del producto, y (c) mejorar la experiencia del usuario. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines comerciales.'
  }, {
    heading: '3. Protección de datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Toda la transmisión de datos se realiza mediante conexiones cifradas (SSL/TLS).'
  }, {
    heading: '4. Cookies',
    body: 'Este sitio puede utilizar cookies técnicas necesarias para el funcionamiento básico de la página. No utilizamos cookies de seguimiento publicitario de terceros sin tu consentimiento previo.'
  }, {
    heading: '5. Tus derechos',
    body: 'Tienes derecho a acceder, rectificar o solicitar la eliminación de tus datos personales en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales indicados en la plataforma de compra.'
  }, {
    heading: '6. Retención de datos',
    body: 'Conservamos tu información personal únicamente durante el tiempo necesario para cumplir con los fines descritos en esta política, o según lo exija la legislación aplicable.'
  }, {
    heading: '7. Cambios a esta política',
    body: 'Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. Los cambios entrarán en vigor al momento de su publicación en este sitio.'
  }]
};
const TERMS_CONTENT = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los términos',
    body: 'Al adquirir el Maletín de Calma Escolar, aceptas plenamente estos Términos y Condiciones. Si no estás de acuerdo con alguno de los términos aquí establecidos, te pedimos que no realices la compra.'
  }, {
    heading: '2. Naturaleza del producto',
    body: 'El Maletín de Calma Escolar es un producto digital de carácter educativo e informativo. Su contenido está diseñado para apoyar a padres y cuidadores en el manejo de situaciones de desregulación emocional en niños de 9 a 13 años. Este producto NO sustituye el diagnóstico, tratamiento ni la opinión de un profesional de la salud mental, psicólogo, psiquiatra, neurólogo o cualquier otro especialista clínico.'
  }, {
    heading: '3. Limitación de responsabilidad',
    body: 'Los resultados descritos en este material son orientativos y pueden variar según cada niño y contexto familiar. No garantizamos resultados específicos. El uso de las técnicas descritas es responsabilidad exclusiva del usuario. En caso de que el niño presente conductas de riesgo, violencia grave o síntomas que requieran atención clínica, se recomienda buscar ayuda profesional de inmediato.'
  }, {
    heading: '4. Propiedad intelectual',
    body: 'Todo el contenido del Maletín de Calma Escolar, incluyendo textos, metodologías, ilustraciones y materiales complementarios, está protegido por derechos de autor. Queda estrictamente prohibida su reproducción, distribución, modificación o venta total o parcial sin autorización expresa y por escrito del autor.'
  }, {
    heading: '5. Uso personal',
    body: 'La licencia de uso otorgada al comprador es personal, intransferible y no exclusiva. El producto está destinado al uso individual del comprador y su núcleo familiar directo. No está permitido compartir, revender ni distribuir el archivo a terceros.'
  }, {
    heading: '6. Política de reembolso',
    body: 'Dado que se trata de un producto digital con acceso inmediato, no se realizan reembolsos una vez que el archivo ha sido descargado o el acceso ha sido habilitado, salvo en casos de error técnico imputable al vendedor debidamente comprobado.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones serán efectivas desde su publicación. El uso continuado del producto implica la aceptación de los términos vigentes.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  const content = type === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT;
  return <AnimatePresence data-id="element-15">
      {type && <>
          {/* Backdrop */}
          <motion.div key="backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} aria-hidden="true" data-id="element-16" />
          {/* Modal */}
          <motion.div key="modal" initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed inset-x-4 top-[5%] bottom-[5%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl z-50 flex flex-col" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-17">
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden" data-id="element-18">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#0d7377]/10 flex-shrink-0" data-id="element-19">
                <div className="flex items-center gap-2" data-id="element-20">
                  {type === 'privacy' ? <ShieldCheckIcon className="w-5 h-5 text-[#0d7377]" data-id="element-21" /> : <FileTextIcon className="w-5 h-5 text-[#0d7377]" data-id="element-22" />}
                  <h2 id="modal-title" className="text-base md:text-lg font-900 text-[#0d7377]" data-id="element-23">
                    {content.title}
                  </h2>
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-[#5a5a72] hover:bg-[#e8f5f5] hover:text-[#0d7377] transition-colors" aria-label="Cerrar" data-id="element-24">
                  <X className="w-4 h-4" data-id="element-25" />
                </button>
              </div>
              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5" data-id="element-26">
                {content.sections.map(section => <div key={section.heading} data-id="element-27">
                    <h3 className="font-800 text-[#1c1c2e] text-sm md:text-base mb-1.5" data-id="element-28">
                      {section.heading}
                    </h3>
                    <p className="text-[#5a5a72] text-sm leading-relaxed" data-id="element-29">
                      {section.body}
                    </p>
                  </div>)}
                {/* Disclaimer at bottom */}
                <div className="mt-4 p-4 bg-[#e8f5f5] rounded-xl border border-[#0d7377]/15" data-id="element-30">
                  <p className="text-xs text-[#5a5a72] leading-relaxed text-center" data-id="element-31">
                    <strong className="text-[#0d7377]" data-id="element-32">
                      Aviso importante:
                    </strong>{' '}
                    El Maletín de Calma Escolar es un recurso educativo y no
                    sustituye la evaluación ni el tratamiento de un profesional
                    de la salud mental.
                  </p>
                </div>
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-[#0d7377]/10 flex-shrink-0" data-id="element-33">
                <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-[#0d7377] text-white font-800 text-sm hover:bg-[#0a5c60] transition-colors" data-id="element-34">
                  Entendido, cerrar
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function LandingPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <main className="min-h-screen w-full bg-[#fdf9f6]" style={{
    fontFamily: 'Nunito, sans-serif'
  }} data-id="element-35">
      {/* Legal Modal */}
      <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-36" />

      {/* ── HERO ── */}
      <section className="w-full bg-gradient-to-b from-[#e8f5f5] to-[#fdf9f6] pt-10 pb-12 px-4" data-id="element-37">
        <div className="max-w-2xl mx-auto" data-id="element-38">
          {/* Urgency badge */}
          <motion.div initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex justify-center mb-6" data-id="element-39">
            <span className="inline-flex items-center gap-2 bg-[#e8735a]/15 text-[#e8735a] font-800 text-sm px-4 py-2 rounded-full border border-[#e8735a]/30" data-id="element-40">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-41" />
              Para mamás con hijos de 9 a 13 años
            </span>
          </motion.div>

          {/* H1 */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-2xl md:text-4xl font-900 text-[#0d7377] text-center leading-tight mb-5" data-id="element-42">
            ¿Tu hijo tiene explosiones emocionales en el colegio y temes que la
            próxima llamada sea para una{' '}
            <span className="text-[#e8735a]" data-id="element-43">suspensión?</span>
          </motion.h1>

          {/* H2 */}
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-base md:text-xl font-700 text-[#5a5a72] text-center leading-relaxed mb-8" data-id="element-44">
            El método de{' '}
            <span className="text-[#e8735a] font-800" data-id="element-45">
              "Autorregulación Invisible"
            </span>{' '}
            para que los niños de 9 a 13 años recuperen el control en el salón
            de clases{' '}
            <span className="text-[#0d7377] font-800" data-id="element-46">
              sin que nadie note que están usando una técnica de calma.
            </span>
          </motion.h2>

          {/* Product image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex justify-center mb-8" data-id="element-47">
            <img src="https://cdn.magicpatterns.com/uploads/vk5U5dkPW4bJLMCoDMmHFa/MALETIN_DE_CALMA_ESCOLAR.png" alt="Maletín de Calma Escolar - guía digital para padres" className="w-full max-w-md rounded-2xl shadow-2xl shadow-[#0d7377]/20" data-id="element-48" />
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} data-id="element-49">
            <CTAButton data-id="element-50" />
            <p className="text-center text-xs text-[#5a5a72] mt-3 flex items-center justify-center gap-1" data-id="element-51">
              <LockIcon className="w-3 h-3" data-id="element-52" /> Compra segura · Acceso
              instantáneo en PDF
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── OPENING HOOK ── */}
      <section className="w-full px-4 py-10" data-id="element-53">
        <div className="max-w-2xl mx-auto" data-id="element-54">
          <FadeIn data-id="element-55">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-[#0d7377]/10" data-id="element-56">
              <p className="text-base md:text-lg text-[#1c1c2e] leading-relaxed mb-4" data-id="element-57">
                Sabes exactamente de qué hablo. Estás en el trabajo o haciendo
                las compras y ves el nombre del colegio en la pantalla de tu
                celular a las{' '}
                <strong className="text-[#e8735a]" data-id="element-58">11:00 AM</strong>. Se te
                detiene el corazón.
              </p>
              <p className="text-base md:text-lg text-[#1c1c2e] leading-relaxed" data-id="element-59">
                No es solo un reporte más. Es la vergüenza frente a otros
                padres, el miedo a que lo etiqueten como{' '}
                <strong className="text-[#e8735a]" data-id="element-60">"el niño problema"</strong>{' '}
                y, sobre todo, la impotencia de saber que tu hijo sufre y tú no
                puedes estar ahí para calmarlo.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="w-full px-4 py-10 bg-[#e8f5f5]" data-id="element-61">
        <div className="max-w-2xl mx-auto" data-id="element-62">
          <FadeIn data-id="element-63">
            <div className="flex items-center gap-3 mb-6" data-id="element-64">
              <BrainIcon className="w-7 h-7 text-[#0d7377] flex-shrink-0" data-id="element-65" />
              <h2 className="text-xl md:text-2xl font-900 text-[#0d7377]" data-id="element-66">
                ¿Por qué pasa esto?
              </h2>
            </div>
            <p className="text-base md:text-lg text-[#1c1c2e] leading-relaxed mb-4" data-id="element-67">
              Muchos niños entre los 9 y 13 años atraviesan un{' '}
              <strong className="text-[#e8735a]" data-id="element-68">"caos sensorial"</strong> en la
              escuela. El ruido del salón, la presión de los exámenes y los
              roces con amigos saturan su sistema nervioso.
            </p>
            <p className="text-base md:text-lg text-[#1c1c2e] leading-relaxed" data-id="element-69">
              Cuando esto pasa, su cerebro racional se apaga.{' '}
              <strong data-id="element-70">No es que "no quiera" portarse bien</strong>; es que su
              cuerpo entró en modo de supervivencia. Los castigos en casa no
              funcionan porque llegan horas tarde, y pedirle que "se calme" solo
              lo irrita más porque{' '}
              <strong className="text-[#0d7377]" data-id="element-71">
                no sabe CÓMO hacerlo bajo presión.
              </strong>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── WHY PREVIOUS ATTEMPTS FAILED ── */}
      <section className="w-full px-4 py-10" data-id="element-72">
        <div className="max-w-2xl mx-auto" data-id="element-73">
          <FadeIn data-id="element-74">
            <h2 className="text-xl md:text-2xl font-900 text-[#1c1c2e] mb-6 text-center" data-id="element-75">
              Por qué lo que intentaste{' '}
              <span className="text-[#e8735a]" data-id="element-76">no funcionó</span>
            </h2>
          </FadeIn>
          <div className="space-y-3" data-id="element-77">
            <FadeIn delay={0.05} data-id="element-78">
              <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-red-100 shadow-sm" data-id="element-79">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-80" />
                <p className="text-sm md:text-base text-[#1c1c2e] leading-relaxed" data-id="element-81">
                  <strong data-id="element-82">Los castigos después de la escuela</strong> no enseñan
                  regulación, solo generan resentimiento y bajan su autoestima.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-83">
              <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-red-100 shadow-sm" data-id="element-84">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-85" />
                <p className="text-sm md:text-base text-[#1c1c2e] leading-relaxed" data-id="element-86">
                  <strong data-id="element-87">Las técnicas de calma "obvias"</strong> (como respirar
                  profundo ruidosamente) les dan vergüenza frente a sus amigos y
                  por eso no las usan.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} data-id="element-88">
              <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-red-100 shadow-sm" data-id="element-89">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-90" />
                <p className="text-sm md:text-base text-[#1c1c2e] leading-relaxed" data-id="element-91">
                  <strong data-id="element-92">Las promesas de premios</strong> funcionan un rato,
                  pero no sirven cuando el niño ya está en medio de una crisis
                  emocional.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-93">
              <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-red-100 shadow-sm" data-id="element-94">
                <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-95" />
                <p className="text-sm md:text-base text-[#1c1c2e] leading-relaxed" data-id="element-96">
                  <strong data-id="element-97">El sistema escolar tradicional</strong> está diseñado
                  para la obediencia, no para entender cerebros desregulados.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <SectionDivider data-id="element-98" />

      {/* ── UNIQUE MECHANISM ── */}
      <section className="w-full px-4 py-10" data-id="element-99">
        <div className="max-w-2xl mx-auto" data-id="element-100">
          <FadeIn data-id="element-101">
            <div className="bg-gradient-to-br from-[#0d7377] to-[#0a5c60] rounded-2xl p-6 md:p-8 text-white shadow-xl" data-id="element-102">
              <div className="flex items-center gap-3 mb-4" data-id="element-103">
                <ShieldIcon className="w-7 h-7 text-[#e8735a]" data-id="element-104" />
                <h2 className="text-xl md:text-2xl font-900" data-id="element-105">
                  El Método de Autorregulación Invisible
                </h2>
              </div>
              <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-106">
                He diseñado un sistema de herramientas tácticas que el niño
                ejecuta desde su propio pupitre,{' '}
                <strong className="text-[#e8735a]" data-id="element-107">
                  en silencio y sin llamar la atención.
                </strong>{' '}
                Son ejercicios de micro-presión, anclaje visual y propiocepción
                que envían una señal de seguridad al cerebro en{' '}
                <strong className="text-white" data-id="element-108">menos de 60 segundos</strong>,
                permitiendo que el niño se mantenga en clase sin explotar.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── WHAT YOU GET ── */}
      <section className="w-full px-4 py-10 bg-[#e8f5f5]" data-id="element-109">
        <div className="max-w-2xl mx-auto" data-id="element-110">
          <FadeIn data-id="element-111">
            <h2 className="text-xl md:text-2xl font-900 text-[#0d7377] text-center mb-8" data-id="element-112">
              ¿Qué incluye el Maletín?
            </h2>
          </FadeIn>
          <div className="space-y-4" data-id="element-113">
            <FadeIn delay={0.05} data-id="element-114">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10 flex items-start gap-4" data-id="element-115">
                <div className="w-10 h-10 rounded-xl bg-[#e8735a]/15 flex items-center justify-center flex-shrink-0" data-id="element-116">
                  <ZapIcon className="w-5 h-5 text-[#e8735a]" data-id="element-117" />
                </div>
                <div data-id="element-118">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-119">
                    Interruptor de Pánico
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72] leading-relaxed" data-id="element-120">
                    Identificarás el punto de no retorno antes de la explosión.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-121">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10 flex items-start gap-4" data-id="element-122">
                <div className="w-10 h-10 rounded-xl bg-[#0d7377]/10 flex items-center justify-center flex-shrink-0" data-id="element-123">
                  <BriefcaseIcon className="w-5 h-5 text-[#0d7377]" data-id="element-124" />
                </div>
                <div data-id="element-125">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-126">
                    El Maletín Invisible
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72] leading-relaxed" data-id="element-127">
                    5 técnicas discretas de 30 segundos: Anclaje de 10 dedos,
                    Cuadrado Mental y más.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} data-id="element-128">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10 flex items-start gap-4" data-id="element-129">
                <div className="w-10 h-10 rounded-xl bg-[#e8735a]/15 flex items-center justify-center flex-shrink-0" data-id="element-130">
                  <HeartIcon className="w-5 h-5 text-[#e8735a]" data-id="element-131" />
                </div>
                <div data-id="element-132">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-133">
                    El Código Secreto
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72] leading-relaxed" data-id="element-134">
                    Convierte al maestro de Juez a Aliado con el lenguaje
                    correcto.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-135">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10 flex items-start gap-4" data-id="element-136">
                <div className="w-10 h-10 rounded-xl bg-[#0d7377]/10 flex items-center justify-center flex-shrink-0" data-id="element-137">
                  <SunIcon className="w-5 h-5 text-[#0d7377]" data-id="element-138" />
                </div>
                <div data-id="element-139">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-140">
                    Escudo Anti-Frustración
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72] leading-relaxed" data-id="element-141">
                    El ritual de la mañana que cambiará el mediodía escolar.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.25} data-id="element-142">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10 flex items-start gap-4" data-id="element-143">
                <div className="w-10 h-10 rounded-xl bg-[#e8735a]/15 flex items-center justify-center flex-shrink-0" data-id="element-144">
                  <BackpackIcon className="w-5 h-5 text-[#e8735a]" data-id="element-145" />
                </div>
                <div data-id="element-146">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-147">
                    Auditoría de Mochila
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72] leading-relaxed" data-id="element-148">
                    Lista de herramientas sensoriales de bajo perfil que sí
                    están permitidas en el aula.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR / NOT FOR ── */}
      <section className="w-full px-4 py-10" data-id="element-149">
        <div className="max-w-2xl mx-auto" data-id="element-150">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-151">
            <FadeIn data-id="element-152">
              <div className="bg-[#dcfce7] rounded-2xl p-5 border border-green-200 h-full" data-id="element-153">
                <h3 className="font-900 text-[#16a34a] text-lg mb-4 flex items-center gap-2" data-id="element-154">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-155" />
                  Esto es para ti si...
                </h3>
                <ul className="space-y-3" data-id="element-156">
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-157">
                    <CheckCircleIcon className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" data-id="element-158" />
                    Eres mamá con hijos de 9 a 13 años que reciben reportes
                    constantes por conducta.
                  </li>
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-159">
                    <CheckCircleIcon className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" data-id="element-160" />
                    Tu hijo tiene TDAH, Ansiedad Escolar o Alta Sensibilidad.
                  </li>
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-161">
                    <CheckCircleIcon className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" data-id="element-162" />
                    Sientes que el colegio está a punto de "rendirse" con tu
                    hijo.
                  </li>
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-163">
                    <CheckCircleIcon className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" data-id="element-164" />
                    Buscas soluciones prácticas y rápidas, no teorías largas.
                  </li>
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-165">
              <div className="bg-[#fef2f2] rounded-2xl p-5 border border-red-200 h-full" data-id="element-166">
                <h3 className="font-900 text-red-500 text-lg mb-4 flex items-center gap-2" data-id="element-167">
                  <XCircleIcon className="w-5 h-5" data-id="element-168" />
                  No es para ti si...
                </h3>
                <ul className="space-y-3" data-id="element-169">
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-170">
                    <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-171" />
                    Buscas una solución mágica sin involucrarte en la práctica
                    con el niño.
                  </li>
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-172">
                    <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-173" />
                    Son casos de violencia física grave que requieren
                    intervención clínica inmediata.
                  </li>
                  <li className="flex items-start gap-2 text-sm md:text-base text-[#1c1c2e]" data-id="element-174">
                    <XCircleIcon className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-175" />
                    Crees que la disciplina solo se logra mediante el miedo y el
                    castigo físico.
                  </li>
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="w-full px-4 py-10 bg-[#e8f5f5]" data-id="element-176">
        <div className="max-w-2xl mx-auto" data-id="element-177">
          <FadeIn data-id="element-178">
            <h2 className="text-xl md:text-2xl font-900 text-[#0d7377] text-center mb-8" data-id="element-179">
              ¿Cómo funciona?
            </h2>
          </FadeIn>
          <div className="space-y-4" data-id="element-180">
            <FadeIn delay={0.05} data-id="element-181">
              <div className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10" data-id="element-182">
                <div className="w-10 h-10 rounded-full bg-[#0d7377] text-white font-900 text-lg flex items-center justify-center flex-shrink-0" data-id="element-183">
                  1
                </div>
                <div data-id="element-184">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-185">
                    Descarga el Maletín
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72]" data-id="element-186">
                    Lee la guía en 30 minutos. Está diseñada para mamás sin
                    tiempo.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-187">
              <div className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10" data-id="element-188">
                <div className="w-10 h-10 rounded-full bg-[#e8735a] text-white font-900 text-lg flex items-center justify-center flex-shrink-0" data-id="element-189">
                  2
                </div>
                <div data-id="element-190">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-191">
                    Practica en Casa
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72]" data-id="element-192">
                    Enseña las 5 técnicas invisibles a tu hijo como si fuera un{' '}
                    <strong className="text-[#0d7377]" data-id="element-193">
                      "entrenamiento de agente secreto"
                    </strong>
                    .
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} data-id="element-194">
              <div className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-[#0d7377]/10" data-id="element-195">
                <div className="w-10 h-10 rounded-full bg-[#0d7377] text-white font-900 text-lg flex items-center justify-center flex-shrink-0" data-id="element-196">
                  3
                </div>
                <div data-id="element-197">
                  <h3 className="font-800 text-[#1c1c2e] mb-1" data-id="element-198">
                    Equipa la Mochila
                  </h3>
                  <p className="text-sm md:text-base text-[#5a5a72]" data-id="element-199">
                    Aplica la auditoría y envía el mensaje sugerido al maestro
                    para coordinar el apoyo.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── PROOF / RESULTS ── */}
      <section className="w-full px-4 py-10" data-id="element-200">
        <div className="max-w-2xl mx-auto" data-id="element-201">
          <FadeIn data-id="element-202">
            <div className="bg-gradient-to-br from-[#0d7377] to-[#0a5c60] rounded-2xl p-6 md:p-8 text-white shadow-xl" data-id="element-203">
              <div className="flex items-center gap-3 mb-4" data-id="element-204">
                <ClockIcon className="w-7 h-7 text-[#e8735a]" data-id="element-205" />
                <h2 className="text-xl md:text-2xl font-900" data-id="element-206">
                  Lo que cambia en 7 días
                </h2>
              </div>
              <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-207">
                En la primera semana, notarás que los reportes diarios
                disminuyen porque tu hijo ya no llega al{' '}
                <strong className="text-[#e8735a]" data-id="element-208">
                  "punto de no retorno"
                </strong>
                . Al tener un plan de salida, su ansiedad escolar baja y
                recupera la confianza de que{' '}
                <strong className="text-white" data-id="element-209">
                  él puede controlar su cuerpo
                </strong>
                , no al revés.
              </p>
              <div className="mt-5 flex items-center gap-1" data-id="element-210">
                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-211" />
                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-212" />
                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-213" />
                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-214" />
                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-215" />
                <span className="text-white/80 text-sm ml-2" data-id="element-216">
                  Resultados reales de mamás como tú
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="w-full px-4 py-10 bg-[#e8f5f5]" data-id="element-217">
        <div className="max-w-2xl mx-auto" data-id="element-218">
          <FadeIn data-id="element-219">
            <h2 className="text-xl md:text-2xl font-900 text-[#0d7377] text-center mb-6" data-id="element-220">
              Preguntas frecuentes
            </h2>
          </FadeIn>
          <FadeIn delay={0.05} data-id="element-221">
            <div className="space-y-3" data-id="element-222">
              <FAQItem question="¿Mi hijo de 12 años querrá hacer esto?" answer="Sí, porque las técnicas son 'invisibles' y no lo exponen frente a sus amigos. Las presentamos como un entrenamiento de agente secreto, lo que lo hace atractivo para niños de esta edad." data-id="element-223" />
              <FAQItem question="¿Tengo que hablar con el colegio?" answer="Te damos los guiones exactos para que esa charla sea breve, profesional y efectiva. No tendrás que improvisar ni sentirte en desventaja." data-id="element-224" />
              <FAQItem question="¿Sirve para niños con TDAH?" answer="Especialmente para ellos, ya que el método ayuda a canalizar la energía motora de forma productiva. Las técnicas de micro-presión y propiocepción son particularmente efectivas para cerebros con TDAH." data-id="element-225" />
              <FAQItem question="¿Es un libro largo?" answer="No, es un manual de acción estratégica de lectura rápida. Está diseñado para que puedas leerlo en 30 minutos y empezar a aplicarlo ese mismo día." data-id="element-226" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── OFFER + PRICE BLOCK ── */}
      <section id="oferta" className="w-full px-4 py-12" data-id="element-227">
        <div className="max-w-2xl mx-auto" data-id="element-228">
          <FadeIn data-id="element-229">
            <div className="bg-white rounded-3xl p-6 md:p-10 shadow-2xl shadow-[#0d7377]/10 border-2 border-[#0d7377]/20 text-center" data-id="element-230">
              {/* Product image small */}
              <img src="https://cdn.magicpatterns.com/uploads/vk5U5dkPW4bJLMCoDMmHFa/MALETIN_DE_CALMA_ESCOLAR.png" alt="Maletín de Calma Escolar" className="w-40 md:w-52 mx-auto rounded-xl shadow-lg mb-6" data-id="element-231" />

              <h2 className="text-xl md:text-2xl font-900 text-[#0d7377] mb-2" data-id="element-232">
                Maletín de Calma Escolar
              </h2>
              <p className="text-[#5a5a72] text-sm md:text-base mb-6 leading-relaxed" data-id="element-233">
                Recupera la paz de tus mañanas y protege la reputación escolar
                de tu hijo hoy mismo. Acceso inmediato al Maletín de Calma
                Escolar por una inversión única.
              </p>

              {/* Discount badge */}
              <div className="flex justify-center mb-4" data-id="element-234">
                <span className="inline-block bg-[#e8735a] text-white font-900 text-sm px-4 py-1.5 rounded-full" data-id="element-235">
                  66.36% OFF — Oferta por tiempo limitado
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-236">
                <span className="text-2xl text-[#5a5a72] line-through font-700" data-id="element-237">
                  $19 USD
                </span>
                <span className="text-4xl md:text-5xl font-900 text-[#16a34a]" data-id="element-238">
                  $6.39 USD
                </span>
              </div>

              {/* CTA */}
              <CTAButton label="¡QUIERO EL MALETÍN AHORA!" data-id="element-239" />

              {/* Trust badges */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-xs text-[#5a5a72]" data-id="element-240">
                <span className="flex items-center gap-1" data-id="element-241">
                  <LockIcon className="w-3.5 h-3.5 text-[#16a34a]" data-id="element-242" />
                  Compra 100% segura
                </span>
                <span className="flex items-center gap-1" data-id="element-243">
                  <SmartphoneIcon className="w-3.5 h-3.5 text-[#0d7377]" data-id="element-244" />
                  Acceso instantáneo en PDF
                </span>
                <span className="flex items-center gap-1" data-id="element-245">
                  <CheckCircleIcon className="w-3.5 h-3.5 text-[#16a34a]" data-id="element-246" />
                  Compatible con cualquier celular
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="w-full px-4 pt-8 pb-6 bg-[#1c1c2e]" data-id="element-247">
        <div className="max-w-2xl mx-auto space-y-5" data-id="element-248">
          {/* Disclaimer */}
          <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-center" data-id="element-249">
            <p className="text-white/70 text-xs leading-relaxed" data-id="element-250">
              <strong className="text-white/90" data-id="element-251">⚠️ Aviso importante:</strong> El
              Maletín de Calma Escolar es un recurso educativo de apoyo
              parental. Su contenido{' '}
              <strong className="text-white/90" data-id="element-252">
                no sustituye la opinión, diagnóstico ni tratamiento de un
                profesional de la salud mental
              </strong>{' '}
              (psicólogo, psiquiatra, neurólogo u otro especialista clínico). Si
              tu hijo presenta conductas de riesgo o síntomas graves, consulta a
              un profesional de inmediato.
            </p>
          </div>

          {/* Links */}
          <div className="flex items-center justify-center gap-6" data-id="element-253">
            <button onClick={() => setActiveModal('privacy')} className="flex items-center gap-1.5 text-white/50 hover:text-white/80 text-xs transition-colors" data-id="element-254">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-255" />
              Política de Privacidad
            </button>
            <span className="text-white/20 text-xs" data-id="element-256">·</span>
            <button onClick={() => setActiveModal('terms')} className="flex items-center gap-1.5 text-white/50 hover:text-white/80 text-xs transition-colors" data-id="element-257">
              <FileTextIcon className="w-3.5 h-3.5" data-id="element-258" />
              Términos y Condiciones
            </button>
          </div>

          {/* Copyright */}
          <p className="text-white/30 text-xs text-center" data-id="element-259">
            © 2025 Maletín de Calma Escolar · Todos los derechos reservados
          </p>
        </div>
      </footer>
    </main>;
}